import { CardContainer } from '../CardContainer';
import type { MonthRevenue } from '../../services/api';

const MONTH_LABELS: Record<string, string> = {
  '01': 'Ene', '02': 'Feb', '03': 'Mar', '04': 'Abr',
  '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Ago',
  '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dic',
};

const MONTH_LABELS_SHORT: Record<string, string> = {
  '01': 'E', '02': 'F', '03': 'M', '04': 'A',
  '05': 'M', '06': 'J', '07': 'J', '08': 'A',
  '09': 'S', '10': 'O', '11': 'N', '12': 'D',
};

const formatCurrency = (amount: number) =>
  `Q ${amount.toLocaleString('es-GT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

interface Props {
  months: MonthRevenue[];
  yearTotal: number;
  monthlyAvg: number;
  bestMonth: MonthRevenue;
  yoYChangePct: number;
  currency: string;
  year: number;
}

export const RevenueTrendChart = ({
  months,
  yearTotal,
  monthlyAvg,
  bestMonth,
  yoYChangePct: _yoYChangePct,
  year,
}: Props) => {
  const maxRevenue = Math.max(...months.map((m) => m.revenue), 1);
  const avgRatio = monthlyAvg / maxRevenue;

  const currentMonthKey = `${year}-${String(new Date().getFullYear() === year ? new Date().getMonth() + 1 : -1).padStart(2, '0')}`;

  // Current month revenue and MoM comparison
  const currentMonthIdx = months.findIndex((m) => m.month === currentMonthKey);
  const currentMonthRevenue = currentMonthIdx >= 0 ? months[currentMonthIdx].revenue : 0;
  const prevMonthRevenue = currentMonthIdx > 0 ? months[currentMonthIdx - 1].revenue : null;
  const momChangePct =
    prevMonthRevenue != null && prevMonthRevenue > 0
      ? ((currentMonthRevenue - prevMonthRevenue) / prevMonthRevenue) * 100
      : prevMonthRevenue === 0 && currentMonthRevenue > 0
      ? 100
      : null;
  const isPositiveMoM = momChangePct !== null && momChangePct >= 0;

  return (
    <CardContainer padding="p-8">
      {/* Header */}
      <div className="flex justify-between items-start mb-8">
        <div className="flex flex-col gap-1">
          <h3 className="text-slate-900 text-xl font-bold">Tendencia de Ingresos</h3>
          <p className="text-slate-500 text-sm">Ingresos mensuales — {year}</p>
        </div>
        <div className="flex flex-col items-end">
          <span className="text-3xl font-black text-slate-900 tracking-tight">
            {formatCurrency(currentMonthRevenue)}
          </span>
          {momChangePct !== null && (
            <span
              className={`text-sm font-bold flex items-center gap-1 ${
                isPositiveMoM ? 'text-emerald-500' : 'text-rose-500'
              }`}
            >
              <span className="material-symbols-outlined text-sm">
                {isPositiveMoM ? 'trending_up' : 'trending_down'}
              </span>
              {isPositiveMoM ? '+' : ''}
              {momChangePct.toFixed(1)}% vs mes previo
            </span>
          )}
        </div>
      </div>

      {/* Bar chart */}
      <div className="relative h-64 flex items-end justify-between gap-0 sm:gap-2 px-0 sm:px-2">
        {/* Average dashed line */}
        <div
          className="absolute left-0 w-full border-t-2 border-dashed border-slate-200 z-0 flex items-center pointer-events-none"
          style={{ bottom: `calc(${(avgRatio * 100).toFixed(1)}% + 2rem)` }}
        >
          <span className="bg-white px-2 text-[10px] text-slate-400 font-bold uppercase tracking-widest ml-4">
            Promedio Mensual
          </span>
        </div>

        {/* Bars */}
        {months.map((m) => {
          const key = m.month.slice(5, 7);
          const monthLabel = MONTH_LABELS[key] ?? key;
          const monthLabelShort = MONTH_LABELS_SHORT[key] ?? key;
          const heightPct = maxRevenue > 0 ? (m.revenue / maxRevenue) * 100 : 0;
          const isCurrent = m.month === currentMonthKey;

          return (
            <div
              key={m.month}
              className="flex-1 flex flex-col items-center h-full justify-end group cursor-pointer"
              title={`${monthLabel}: ${formatCurrency(m.revenue)}`}
            >
              <div
                className={`w-full rounded-t-sm transition-all ${
                  isCurrent
                    ? 'bg-primary shadow-lg shadow-primary/20'
                    : 'bg-primary/20 group-hover:bg-primary/40'
                }`}
                style={{ height: `${Math.max(heightPct, heightPct > 0 ? 2 : 0)}%` }}
              />
              <span
                className={`mt-1 font-bold leading-none ${
                  isCurrent ? 'text-primary' : 'text-slate-400'
                } text-[9px] sm:text-[11px]`}
              >
                <span className="sm:hidden">{monthLabelShort}</span>
                <span className="hidden sm:inline">{monthLabel}</span>
              </span>
            </div>
          );
        })}
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-100 mt-4">
        <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-full border border-slate-100">
          <span className="material-symbols-outlined text-primary text-lg">calendar_today</span>
          <p className="text-slate-700 text-xs font-bold uppercase tracking-wider">
            Total del año:{' '}
            <span className="text-slate-900 ml-1">{formatCurrency(yearTotal)}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-full border border-slate-100">
          <span className="material-symbols-outlined text-primary text-lg">analytics</span>
          <p className="text-slate-700 text-xs font-bold uppercase tracking-wider">
            Promedio mensual:{' '}
            <span className="text-slate-900 ml-1">{formatCurrency(monthlyAvg)}</span>
          </p>
        </div>
        {bestMonth.revenue > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full border border-primary/20">
            <span className="material-symbols-outlined text-primary text-lg">stars</span>
            <p className="text-primary text-xs font-bold uppercase tracking-wider">
              Mejor mes:{' '}
              <span className="font-black ml-1">
                {MONTH_LABELS[bestMonth.month.slice(5, 7)]} {formatCurrency(bestMonth.revenue)}
              </span>
            </p>
          </div>
        )}
      </div>
    </CardContainer>
  );
};
